// src/hooks/useKBChatSession.ts
// NEW_FILE_JUSTIFIED: Encapsulates KB chat session DB persistence logic to keep GlobalAIChatProvider under line limit.
/**
 * Hook for persisting Knowledge Base chat sessions to the database.
 * Manages session lifecycle: find/create session, load messages, persist new messages, delete.
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import type { IChatMessage } from '@/@types/globalChat';

interface IKBChatSessionResult {
  sessionId: Id<'chatSessions'> | null;
  persistedMessages: IChatMessage[];
  persistMessage: (role: 'user' | 'assistant', content: string) => Promise<void>;
  createSessionIfNeeded: () => Promise<Id<'chatSessions'>>;
  deleteSession: () => Promise<void>;
  isLoading: boolean;
}

export function useKBChatSession(
  contextType: string | null,
  orgId: Id<'organisations'> | null
): IKBChatSessionResult {
  const sessions = useQuery(api.chat.queries.getUserSessions);

  const sessionId = useMemo(() => {
    if (!sessions || !contextType) return null;
    const match = sessions.find(s => s.contextType === contextType);
    return match?._id ?? null;
  }, [sessions, contextType]);

  const dbMessages = useQuery(
    api.chat.queries.getSessionMessages,
    sessionId ? { sessionId } : 'skip'
  );

  const persistedMessages = useMemo<IChatMessage[]>(() => {
    if (!dbMessages) return [];
    return dbMessages.map(msg => ({
      id: msg._id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
    }));
  }, [dbMessages]);

  const createSessionMutation = useMutation(api.chat.mutations.createSession);
  const addMessageMutation = useMutation(api.chat.mutations.addMessage);
  const deleteSessionMutation = useMutation(api.chat.mutations.deleteSession);

  // Cache created session ID to avoid duplicate creation between renders
  const createdSessionRef = useRef<Id<'chatSessions'> | null>(null);

  // Sync ref with reactive sessionId (clears when session is deleted)
  useEffect(() => {
    if (sessionId) {
      createdSessionRef.current = sessionId;
    }
  }, [sessionId]);

  const createSessionIfNeeded = useCallback(async (): Promise<Id<'chatSessions'>> => {
    if (sessionId) return sessionId;
    if (createdSessionRef.current) return createdSessionRef.current;
    const entityId = orgId ?? undefined;
    const newSessionId = await createSessionMutation({
      contextType: contextType ?? undefined,
      contextEntityId: entityId,
    });
    createdSessionRef.current = newSessionId;
    return newSessionId;
  }, [sessionId, contextType, orgId, createSessionMutation]);

  const persistMessage = useCallback(
    async (role: 'user' | 'assistant', content: string) => {
      const sid = await createSessionIfNeeded();
      await addMessageMutation({ sessionId: sid, role, content });
    },
    [createSessionIfNeeded, addMessageMutation]
  );

  const deleteSession = useCallback(async () => {
    const sid = sessionId ?? createdSessionRef.current;
    if (sid) {
      await deleteSessionMutation({ sessionId: sid });
      createdSessionRef.current = null;
    }
  }, [sessionId, deleteSessionMutation]);

  const isLoading = sessions === undefined || (sessionId !== null && dbMessages === undefined);

  return {
    sessionId,
    persistedMessages,
    persistMessage,
    createSessionIfNeeded,
    deleteSession,
    isLoading,
  };
}
